import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import { useLanguage } from "../lib/language";
import { BadgeCheck, Store, ArrowRight, Handshake, TrendingUp, Copy, Check } from "lucide-react";

export default function VendorsIndex() {
    const { t } = useLanguage();
    const [state, setState] = useState({ loading: true, list: [] });
    const [partners, setPartners] = useState([]);
    const [banner, setBanner] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        let m = true;
        Promise.all([
            api.get("/vendedores").then((r) => r.data).catch(() => []),
            api.get("/partners/public").then((r) => r.data).catch(() => []),
            api.get("/settings").then((r) => r.data).catch(() => ({})),
        ]).then(([vendors, ps, settings]) => {
            if (!m) return;
            setState({ loading: false, list: Array.isArray(vendors) ? vendors : [] });
            setPartners(Array.isArray(ps) ? ps : []);
            // Only mount the banner when the admin has both enabled it AND uploaded an image.
            if (settings?.vendors_banner_enabled && settings?.vendors_banner_image) {
                setBanner({
                    image: settings.vendors_banner_image,
                    title: settings.vendors_banner_title || "",
                    subtitle: settings.vendors_banner_subtitle || "",
                    cta_label: settings.vendors_banner_cta_label || "",
                    cta_url: settings.vendors_banner_cta_url || "",
                    position: settings.vendors_banner_image_position || "50% 50%",
                });
            }
        });
        return () => { m = false; };
    }, []);

    const list = state.list.filter((v) => {
        if (!q) return true;
        const s = `${v.display_name} ${v.categoria || ""}`.toLowerCase();
        return s.includes(q.toLowerCase());
    });

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="vendors-index-page">
            {/* Admin-controlled featured banner — full-width above the marketplace intro. */}
            {banner && (
                <div className="relative rounded-xl overflow-hidden -mx-4 md:-mx-6" data-testid="vendors-featured-banner">
                    <img src={banner.image} alt={banner.title || "Banner"}
                        style={{ objectPosition: banner.position || "50% 50%" }}
                        className="w-full h-40 sm:h-56 md:h-72 object-cover" />
                    {(banner.title || banner.subtitle || banner.cta_label) && (
                        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent flex flex-col justify-center px-6 md:px-12">
                            {banner.title && (
                                <div className="font-display text-2xl sm:text-3xl md:text-5xl text-white drop-shadow-lg max-w-2xl">
                                    {banner.title}
                                </div>
                            )}
                            {banner.subtitle && (
                                <div className="text-xs sm:text-sm md:text-base text-white/90 mt-2 max-w-lg drop-shadow-lg">
                                    {banner.subtitle}
                                </div>
                            )}
                            {banner.cta_label && banner.cta_url && (
                                <a href={banner.cta_url}
                                    target={banner.cta_url.startsWith("http") ? "_blank" : undefined}
                                    rel={banner.cta_url.startsWith("http") ? "noopener noreferrer" : undefined}
                                    className="inline-block mt-3 self-start px-4 py-2 bg-[var(--tb-gold)] text-black text-xs md:text-sm font-semibold rounded hover:bg-[var(--tb-gold-2)] transition"
                                    data-testid="vendors-featured-banner-cta">
                                    {banner.cta_label}
                                </a>
                            )}
                        </div>
                    )}
                </div>
            )}
            <div className="text-center py-4">
                <div className="tb-overline text-[var(--tb-gold)]">{t("MARKETPLACE", "MARKETPLACE")}</div>
                <h1 className="font-display text-3xl md:text-5xl">{t("Nuestros vendedores", "Our sellers")}</h1>
                <p className="text-[var(--tb-text-2)] mt-2 max-w-xl mx-auto text-sm md:text-base">
                    {t("Emprendedores verificados que venden en nuestra plataforma. Descubre sus tiendas y sigue a tus favoritos.", "Verified entrepreneurs selling on our platform. Discover their stores and follow your favorites.")}
                </p>
            </div>

            <div className="max-w-md mx-auto">
                <input value={q} onChange={(e) => setQ(e.target.value)}
                    className="tb-input" placeholder={t("Buscar por nombre o categoría...", "Search by name or category...")}
                    data-testid="vendors-index-search" />
            </div>

            {state.loading ? (
                <div className="text-center text-[var(--tb-muted)] py-12">{t("Cargando vendedores…", "Loading sellers…")}</div>
            ) : list.length === 0 ? (
                <div className="tb-card p-10 text-center text-[var(--tb-text-2)]" data-testid="vendors-index-empty">
                    <Store className="w-10 h-10 mx-auto mb-3 text-[var(--tb-muted)]" />
                    <p>{q ? t("No hay vendedores que coincidan. Vuelve pronto.", "No matching sellers. Check back soon.") : t("Aún no hay vendedores activos. Vuelve pronto.", "No active sellers yet. Check back soon.")}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vendors-index-grid">
                    {list.map((v) => (
                        <Link key={v.id} to={`/v/${v.id}`}
                            className="tb-card p-4 flex items-start gap-3 hover:border-[var(--tb-gold)] transition-colors group"
                            data-testid={`vendors-index-card-${v.id}`}>
                            {v.foto ? (
                                <img src={v.foto} alt={v.display_name}
                                    className="w-16 h-16 rounded-full object-cover ring-1 ring-[var(--tb-gold)]/40 flex-shrink-0" />
                            ) : (
                                <div className="w-16 h-16 rounded-full bg-[var(--tb-bg-2)] flex items-center justify-center text-xl font-display text-[var(--tb-muted)] flex-shrink-0">
                                    {v.display_name?.[0]?.toUpperCase() || "V"}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-base inline-flex items-center gap-1 group-hover:text-[var(--tb-gold)] transition-colors">
                                    {v.display_name}
                                    {v.is_verified && <BadgeCheck className="w-4 h-4 text-[var(--tb-gold)]" />}
                                </div>
                                {v.categoria && (
                                    <div className="text-[10px] text-[var(--tb-muted)] mt-0.5 uppercase tracking-wide">{v.categoria}</div>
                                )}
                                {v.bio && (
                                    <p className="text-xs text-[var(--tb-text-2)] mt-1.5 line-clamp-2">{v.bio}</p>
                                )}
                                <span className="inline-flex items-center gap-1 text-[11px] text-[var(--tb-gold)] mt-2 opacity-70 group-hover:opacity-100">
                                    {t("Ver tienda", "View store")} <ArrowRight className="w-3 h-3" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Public partners (afiliados) — reconocimiento social a promotores */}
            {partners.length > 0 && (
                <section className="pt-8 border-t border-[var(--tb-border)]" data-testid="vendors-index-partners">
                    <div className="text-center mb-6">
                        <div className="tb-overline text-[var(--tb-gold)] inline-flex items-center justify-center gap-1.5">
                            <Handshake className="w-4 h-4" /> {t("SOCIOS · AFILIADOS", "PARTNERS · AFFILIATES")}
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl mt-1">{t("Nuestros socios", "Our partners")}</h2>
                        <p className="text-[var(--tb-text-2)] mt-2 max-w-xl mx-auto text-sm">
                            {t("Personas que recomiendan Terra Boutique. Usa su código en checkout para reconocerles la venta.", "People who recommend Terra Boutique. Use their code at checkout to credit them for the sale.")}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partners-grid">
                        {partners.map((p) => (
                            <div key={p.id} className="tb-card p-4 flex items-start gap-3" data-testid={`partner-card-${p.code}`}>
                                {p.photo ? (
                                    <img src={p.photo} alt={p.display_name}
                                        className="w-14 h-14 rounded-full object-cover ring-1 ring-[var(--tb-gold)]/40 flex-shrink-0" />
                                ) : (
                                    <div className="w-14 h-14 rounded-full bg-[var(--tb-bg-2)] flex items-center justify-center text-lg font-display text-[var(--tb-muted)] flex-shrink-0">
                                        {p.display_name?.[0]?.toUpperCase() || "S"}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-display text-base flex items-center gap-1">
                                        <span className="truncate">{p.display_name}</span>
                                        {p.verified && <BadgeCheck className="w-4 h-4 text-[#2196F3] fill-[#2196F3]/20 flex-shrink-0" />}
                                    </div>
                                    <PartnerCodeCopyButton code={p.code} />
                                    {p.bio && <p className="text-xs text-[var(--tb-text-2)] mt-1.5 line-clamp-2">{p.bio}</p>}
                                    {p.uses_confirmed > 0 && (
                                        <div className="flex items-center gap-1 mt-2 text-[11px] text-emerald-300">
                                            <TrendingUp className="w-3 h-3" />
                                            {p.uses_confirmed} {p.uses_confirmed === 1 ? t("venta", "sale") : t("ventas", "sales")}
                                            {p.revenue_confirmed > 0 && <span className="text-[var(--tb-muted)]">· {formatPrice(p.revenue_confirmed)}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}


// Small button that copies the partner code in one click and shows an
// inline "Copied!" state for ~1.2s. Placed as a block below the name so it
// always sits on its own line (before it inherited inline-flex from the name).
function PartnerCodeCopyButton({ code }) {
    const { t } = useLanguage();
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            toast.success(t(`Código ${code} copiado`, `Code ${code} copied`));
            setTimeout(() => setCopied(false), 1200);
        } catch {
            toast.error(t("No se pudo copiar", "Could not copy"));
        }
    };
    return (
        <button
            type="button"
            onClick={copy}
            className={`mt-1 inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded border transition ${copied ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-300" : "bg-[var(--tb-gold)]/15 border-[var(--tb-gold)]/40 hover:bg-[var(--tb-gold)]/25"}`}
            title={t("Copiar código", "Copy code")}
            data-testid={`partner-copy-${code}`}>
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {code}
        </button>
    );
}
